import type {
  PromotionRow,
  PromotionType,
} from "@/modules/promotions/domain/types";
import { formatDateLocal } from "@/shared/lib/date-time";
import { DataTable, createSortableColumn, type TanstackReactTable } from "@/shared/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";


import { EditIcon } from "lucide-react";

const TYPE_LABELS: Record<PromotionType, string> = {
  PERCENT: "ສ່ວນຫຼຸດເປີ້ cent (%)",
  FIXED: "ສ່ວນຫຼຸດຄົງທີ່ (LAK)",
  BXGY: "ຊື້ X ແບບ Y",
  SET: "ຊຸດລາຄາພິເສດ",
};

type PromotionsTableProps = {
  data: PromotionRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onEdit: (row: PromotionRow) => void;
};

export function PromotionsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onSortingChange,
  onPaginationChange,
  onEdit,
}: PromotionsTableProps) {
  const columns: TanstackReactTable.ColumnDef<PromotionRow>[] = [
    createSortableColumn<PromotionRow>("name", "ຊື່ໂປຣໂມຊັນ", { size: 220 }),
    {
      id: "type",
      header: "ປະເພດ",
      size: 180,
      cell: ({ row }) => {
        const r = row.original;
        const label = TYPE_LABELS[r.type] ?? r.type;
        if (r.type === "PERCENT" && r.percentValue != null) {
          return (
            <span className="text-muted-foreground text-sm">
              {label} — {r.percentValue}%
            </span>
          );
        }
        if (r.type === "FIXED" && r.fixedAmount != null) {
          return (
            <span className="text-muted-foreground text-sm">
              {label} — {(r.fixedAmount / 1000).toLocaleString()}K
            </span>
          );
        }
        return (
          <span className="text-muted-foreground text-sm">{label}</span>
        );
      },
      enableSorting: false,
    },
    createSortableColumn<PromotionRow>("startAt", "ວັນເລີ່ມ", {
      size: 110,
      cell: ({ row }) => formatDateLocal(row.original.startAt),
    }),
    createSortableColumn<PromotionRow>("endAt", "ວັນສິ້ນສຸດ", {
      size: 110,
      cell: ({ row }) => formatDateLocal(row.original.endAt),
    }),
    {
      id: "isActive",
      header: "ສະຖານະ",
      size: 110,
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? "default" : "secondary"}>
          {row.original.isActive ? "ເປີດໃຊ້ງານ" : "ປິດໃຊ້ງານ"}
        </Badge>
      ),
      enableSorting: false,
    },
    createSortableColumn<PromotionRow>("priority", "ລຳດັບ", { size: 80 }),
    {
      id: "sendPush",
      header: "Push",
      size: 100,
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5 text-xs">
          <Badge variant={row.original.sendPush ? "default" : "outline"}>
            {row.original.sendPush ? "ສົ່ງ" : "—"}
          </Badge>
          {row.original.pushSentAt ? (
            <span className="text-muted-foreground">
              {formatDateLocal(row.original.pushSentAt)}
            </span>
          ) : null}
        </div>
      ),
      enableSorting: false,
    },
    {
      id: "actions",
      header: "",
      size: 100,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(row.original)}
          title="ແກ້ໄຂ"
        >
          <EditIcon className="size-4" />
          ແກ້ໄຂ
        </Button>
      ),
      enableSorting: false,
    },
  ];

  return (
    <DataTable<PromotionRow, unknown>
      noDataMessage="ບໍ່ພົບໂປຣໂມຊັນ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
